import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from './config.js';

/**
 * The leaderboard transport: plain PostgREST calls over `fetch`.
 *
 * The game only needs "append a run" and "read the top ten", so pulling in the
 * full Supabase client (and its ~100 kB) would not pay for itself. Every call
 * here returns a result object instead of throwing, so a dead network can never
 * interrupt a run.
 */

const TABLE = `${SUPABASE_URL}/rest/v1/scores`;
const COLUMNS = 'id,name,score,device,browser,played_at';

/** How many rows the board shows. */
export const LEADERBOARD_SIZE = 10;

/** The longest name the table accepts (mirrors the input's maxlength). */
export const MAX_NAME_LENGTH = 14;

/** True when both project values are filled in. */
export function isConfigured() {
  return Boolean(SUPABASE_URL && SUPABASE_PUBLISHABLE_KEY);
}

function authHeaders() {
  return {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
  };
}

function failure(reason, message) {
  return { ok: false, reason, message };
}

/**
 * Turn a failed response into a reason the UI can explain.
 * `reason` is one of: missing-table, forbidden, rejected, server, network.
 */
function describeFailure(status, payload) {
  const code = payload?.code ?? '';
  if (status === 404 || code === 'PGRST205') {
    return failure(
      'missing-table',
      'The scores table does not exist yet. Run supabase/migrations/20261026120000_create_scores.sql.',
    );
  }
  if (status === 401 || status === 403 || code === '42501') {
    return failure('forbidden', 'The leaderboard refused this entry.');
  }
  if (code === '23514' || code === '22P02') {
    return failure('rejected', 'That entry was rejected as invalid.');
  }
  return failure('server', 'The leaderboard is unavailable right now.');
}

/**
 * Read the device and browser out of a user-agent string.
 *
 * Kept deliberately small: the point is a human-readable "Phone · iOS 17.2" and
 * "Chrome 141" on the board, not exhaustive UA coverage.
 */
export function parseUserAgent(ua = navigator.userAgent) {
  const s = String(ua || '');
  const version = (re) => {
    const match = s.match(re);
    return match?.[1] ? match[1].replace('_', '.') : '';
  };

  let os = '';
  if (/Windows NT 10/.test(s)) os = 'Windows 10/11';
  else if (/Windows NT 6\.3/.test(s)) os = 'Windows 8.1';
  else if (/Windows/.test(s)) os = 'Windows';
  else if (/iPhone|iPad|iPod/.test(s)) os = `iOS ${version(/OS (\d+[_.]\d+)/)}`.trim();
  else if (/Android/.test(s)) os = `Android ${version(/Android (\d+)/)}`.trim();
  else if (/Mac OS X/.test(s)) os = `macOS ${version(/Mac OS X (\d+[_.]\d+)/)}`.trim();
  else if (/CrOS/.test(s)) os = 'ChromeOS';
  else if (/Linux/.test(s)) os = 'Linux';
  else os = 'Unknown';

  const isTablet =
    /iPad|Tablet|PlayBook|Silk/i.test(s) ||
    (/Android/i.test(s) && !/Mobile/i.test(s));
  const isPhone =
    !isTablet && /Mobi|iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(s);
  const kind = isTablet ? 'Tablet' : isPhone ? 'Phone' : 'Desktop';

  let browser = '';
  if (/Edg\//.test(s)) browser = `Edge ${version(/Edg\/(\d+)/)}`;
  else if (/OPR\//.test(s)) browser = `Opera ${version(/OPR\/(\d+)/)}`;
  else if (/SamsungBrowser\//.test(s)) browser = `Samsung ${version(/SamsungBrowser\/(\d+)/)}`;
  else if (/FxiOS\//.test(s)) browser = `Firefox ${version(/FxiOS\/(\d+)/)}`;
  else if (/Firefox\//.test(s)) browser = `Firefox ${version(/Firefox\/(\d+)/)}`;
  else if (/CriOS\//.test(s)) browser = `Chrome ${version(/CriOS\/(\d+)/)}`;
  else if (/Chrome\//.test(s)) browser = `Chrome ${version(/Chrome\/(\d+)/)}`;
  else if (/Safari\//.test(s)) browser = `Safari ${version(/Version\/(\d+(?:\.\d+)?)/)}`;
  else browser = 'Unknown';

  return { device: `${kind} · ${os}`, browser: browser.trim() };
}

/** Trim and clamp a player name to what the table will accept. */
export function normalizeName(name) {
  const clean = String(name ?? '').trim().replace(/\s+/g, ' ');
  if (!clean) return 'PLAYER 1';
  return clean.slice(0, MAX_NAME_LENGTH);
}

function normalizeScore(score) {
  const value = Math.floor(Number(score));
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Append one finished run.
 * Resolves to `{ ok: true, entry, device, browser }` or a `failure(...)`.
 *
 * `userAgent` exists so a non-browser caller (the live check) can describe the
 * device it is pretending to be; in the game it is left undefined and read
 * from `navigator`.
 */
export async function submitScore({
  name,
  score,
  playedAt = new Date(),
  userAgent,
} = {}) {
  if (!isConfigured()) {
    return failure('unconfigured', 'The leaderboard is not configured.');
  }

  const { device, browser } = parseUserAgent(userAgent);
  const row = {
    name: normalizeName(name),
    score: normalizeScore(score),
    device,
    browser,
    played_at: playedAt.toISOString(),
  };

  let response;
  try {
    response = await fetch(`${TABLE}?select=${COLUMNS}`, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify([row]),
    });
  } catch {
    return failure('network', 'Could not reach the leaderboard.');
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    return describeFailure(response.status, payload);
  }

  const rows = await response.json().catch(() => null);
  return { ok: true, entry: rows?.[0] ?? null, device, browser };
}

/**
 * The top runs, highest first; a tie goes to whoever got there first.
 * Resolves to `{ ok: true, rows }` or a `failure(...)`.
 */
export async function fetchLeaderboard(limit = LEADERBOARD_SIZE) {
  if (!isConfigured()) {
    return failure('unconfigured', 'The leaderboard is not configured.');
  }

  const query =
    `select=${COLUMNS}&order=score.desc,played_at.asc&limit=${Math.max(1, limit)}`;

  let response;
  try {
    response = await fetch(`${TABLE}?${query}`, { headers: authHeaders() });
  } catch {
    return failure('network', 'Could not reach the leaderboard.');
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    return describeFailure(response.status, payload);
  }

  const rows = await response.json().catch(() => null);
  return { ok: true, rows: Array.isArray(rows) ? rows : [] };
}

/** Ask PostgREST for a row count using the exact-count header. */
async function countRows(query) {
  try {
    const response = await fetch(`${TABLE}?${query}`, {
      method: 'HEAD',
      headers: { ...authHeaders(), Prefer: 'count=exact' },
    });
    if (!response.ok) return null;
    // content-range looks like "0-9/42", or "*/42" for a HEAD request.
    const total = Number(response.headers.get('content-range')?.split('/')?.[1]);
    return Number.isFinite(total) ? total : null;
  } catch {
    return null;
  }
}

/**
 * Where a score sits on the board: rank counts every strictly higher score, so
 * equal scores share a rank and the earliest entry wins the tie.
 * Resolves to `{ rank, total }` or null when the counts are unavailable.
 */
export async function fetchStanding(score) {
  if (!isConfigured()) return null;

  const value = normalizeScore(score);
  const [better, total] = await Promise.all([
    countRows(`select=id&score=gt.${value}`),
    countRows('select=id'),
  ]);
  if (better === null || total === null) return null;
  return { rank: better + 1, total };
}

/** "just now" / "12m ago" / "3h ago" / "6d ago" / "2026-10-12". */
export function formatWhen(iso, now = Date.now()) {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return '';

  const seconds = Math.max(0, Math.round((now - time) / 1000));
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;

  return new Date(time).toISOString().slice(0, 10);
}
